// lib imports
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

// Core imports.
import AttractionExercise from 'unspoken-core/lib/models/AttractionExercise';
import Project from 'unspoken-core/lib/models/Project';
import Respondent from 'unspoken-core/src/models/Respondent';

// Project imports
import API from '../Api';
import Error from '../components/Error';
import Loading from '../components/Loading';

// Interface describing the props received from the router
interface RouteProps {
  exerciseId: string;
  projectId: string;
}

// Interface describing the props passed to the wrapped component
interface ExerciseDataProps {
  device: 'mobile' | 'desktop';
  exercise: AttractionExercise;
  skipTraining: boolean;
  respondent: Respondent;
  project: Project;
}

const parseQueryString = (queryString: string) : { [key: string]: string } => {
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  return vars.reduce((accummulator, current) => {
    const pair = current.split('=');
    accummulator[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
    return accummulator;
  }, {});
};

/**
 * HOC that extracts URL data, fetches exercise data 
 * from the API and passes that data down to the wrapped
 * component
 */
const withExerciseData = <TOriginalProps extends object>(InnerComponent: React.ComponentType<TOriginalProps & ExerciseDataProps>) => {
  class DataContainer extends React.Component<RouteComponentProps<RouteProps>> {

    // State of the component, to keep track of the recieved data
    public state = {
      device: 'mobile' as 'mobile',
      error: false,
      exercise: null as AttractionExercise | null,
      project: null as Project | null,
      respondent: {} as Respondent,
      skipTraining: false,
    };

    // React lifecycle event: Init component
    public async componentDidMount () {
      // First get the ids from the route params...
      const { projectId, exerciseId } = this.props.match.params;

      // ...Then use those ids to fetch the project and exercise.
      const project = await API.projects.get( projectId );
      const exercise = await API.exercises.get( exerciseId, projectId );
      
      // Extract query params from url
      const queryStringParams = parseQueryString(this.props.location.search);
      const skipTraining = (queryStringParams.skipTraining === '1') || false;
      const device = (queryStringParams.device === 'desktop') ? 'desktop' : 'mobile';
      const respondentId = queryStringParams.hasOwnProperty('respondent') ? queryStringParams.respondent : '';
      const password = queryStringParams.hasOwnProperty('password') ? queryStringParams.password : '';

      // If exercise doesn't exist, enter the error state...
      if ( !project || !exercise ) {
        this.setState({ error: true });
        return;
      }
      
      // fetch all respondents
      const version = await API.exercises.getVersion( exercise.id, project.id );
      if ( version === null ) {
        this.setState({ error: true });
        return;
      }

      
      // instantiate the respondent object
      const respondent : Respondent = {
        createdAt: new Date().toISOString(),
        id: respondentId,
        password,
        swipedStimuli: [],
        version,
      };

      // ...And finally update the state with the exercise and query params
      this.setState({exercise, skipTraining, device, respondent, project});
    }

    // React render method
    public render () {
      if ( this.state.error ) {
        return <Error />;
      } else if ( !this.state.project || !this.state.exercise ) {
        return <Loading />;
      } else {
        const {
          history,
          location,
          match,
          staticContext,
          ...props
        } = this.props;
    
        return (
          <InnerComponent 
            exercise={ this.state.exercise }
            skipTraining={ this.state.skipTraining }
            device={ this.state.device }
            respondent={ this.state.respondent }
            project={ this.state.project }
            {...props}
          />
        );
      }
    }
  }

  return withRouter(DataContainer);
};

export default withExerciseData;



// WEBPACK FOOTER //
// ./src/hoc/withExerciseData.tsx